import { defineStore } from 'pinia'
import { useApplicationEnvironmentStore } from './application-environment'
import { i18n } from 'src/boot/i18n'
import { computed, ComputedRef } from 'vue'
import { LocalStorage } from 'quasar'
import { Locales } from 'src/i18n'

export const useInternationalizationStore = defineStore(
  'internationalization',
  () => {
    const LOCALE_STORAGE_LOCALE_KEY = 'ml-app-locale'
    const applicationEnvironmentStore = useApplicationEnvironmentStore()

    const locale: ComputedRef<Locales> = computed(
      () => i18n.global.locale.value as Locales,
    )

    const standardLocale = computed(() => locale.value.replace('_', '-'))

    const fallbackLocale: ComputedRef<Locales> = computed(
      () => i18n.global.fallbackLocale.value as Locales,
    )

    const locales = computed(
      () => applicationEnvironmentStore.applicationEnvironment?.languages,
    )

    function setLocale(locale: Locales) {
      i18n.global.locale.value = locale
      LocalStorage.set(LOCALE_STORAGE_LOCALE_KEY, locale)
    }

    function initiateFallbackLocale() {
      const fallbackLocale =
        locales.value?.find(
          (locale) => locale === import.meta.env.VITE_FIRST_DEFAULT_LOCALE,
        ) ??
        locales.value?.find(
          (locale) => locale === import.meta.env.VITE_FIRST_DEFAULT_LOCALE,
        )

      if (fallbackLocale) {
        i18n.global.fallbackLocale.value = fallbackLocale
      }
    }

    function initiateLocale() {
      let locale: string | null | undefined = LocalStorage.getItem(
        LOCALE_STORAGE_LOCALE_KEY,
      )?.toString()

      if (!locale || (locale && !locales.value?.includes(locale as Locales))) {
        for (let language of navigator.languages) {
          language = language.replace('-', '_')
          locale = locales.value?.includes(language as Locales)
            ? language
            : locales.value?.find((locale) =>
                locale.startsWith(
                  language.indexOf('_') > 0
                    ? language.substring(0, language.indexOf('_'))
                    : language,
                ),
              )

          if (locale) {
            break
          }
        }
      }

      if (!locale) {
        locale = i18n.global.fallbackLocale.value as string
      }

      setLocale(locale as Locales)
    }

    async function loadMessages() {
      const fallbackLocale: Locales = i18n.global.fallbackLocale
        .value as Locales
      const availableLocales: Locales[] = locales?.value?.length
        ? [...locales.value, fallbackLocale]
        : [fallbackLocale]

      if (availableLocales?.length) {
        for (const locale of availableLocales) {
          if (!locale) continue
          const path = locale.replace('_', '-')

          try {
            const messages = await import(`../i18n/translations/${path}.json`)
            i18n.global.setLocaleMessage(locale, messages.default)
          } catch (err) {
            console.error(`Failed to load ${locale}:`, err)
          }
        }
      }
    }

    return {
      locale,
      standardLocale,
      fallbackLocale,
      locales,
      setLocale,
      initiateFallbackLocale,
      initiateLocale,
      loadMessages,
    }
  },
)
